import { Button } from '@mui/material';
import FullHeightView from 'components/new/FullHeightView';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import {
  revaluation as revaluationRules,
  investment as rules,
} from 'further-ui/validations';
import { PropsWithChildren } from 'react';
import RevaluationForm from 'routes/LpPages/Company/Revaluations/CreateRevaluation';
import { HoldingClassInvestmentTotal } from 'routes/LpPages/Company/Revaluations/CreateRevaluation/types';
import { RevaluationExternalState } from 'routes/LpPages/Company/Revaluations/FormWrapper';
import { InvestmentHistory } from 'routes/LpPages/Company/Revaluations/types';
import { useInvestmentRevaluationSync } from '../hooks/useInvestmentRevaluationSync';

type RevaluationFormSyncProps = {
  revaluationOption?: InvestmentRevaluationOption;
  investmentHistory: InvestmentHistory;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  holdingClasses: Array<{
    holdingClassId?: string;
    investmentAmountTcy: number;
    numberOfUnits: number;
    currency: CurrencyCode;
    unitPriceTcy: number;
  }>;
  onPreviousStep: () => void;
  onSaveDraft: () => void;
  onSaveAndApprove: (data: {
    newRevaluations: rules.CreateInvestmentFormValues['newRevaluations'];
    revaluations: rules.CreateInvestmentFormValues['revaluations'];
    investmentHistory: rules.CreateInvestmentFormValues['investmentHistory'];
  }) => void;
  asset: {
    name: string;
    id: string;
  };
  vehicleId: string;
  viewOnly: boolean;
};

const RevaluationFormContent: React.FC<
  PropsWithChildren<RevaluationFormSyncProps>
> = ({
  children,
  revaluationOption,
  investmentHistory,
  holdingClassInvestmentTotals,
  holdingClasses,
  onPreviousStep,
  onSaveDraft,
  onSaveAndApprove,
  asset,
  vehicleId,
  viewOnly,
}) => {
  const { formMethods } = useFormWithStateContext<
    revaluationRules.RevaluationFormType,
    RevaluationExternalState
  >();

  const { watch, setValue, reset } = formMethods;

  useInvestmentRevaluationSync({
    revaluationOption,
    holdingClasses,
    holdingClassInvestmentTotals,
    investmentHistory,
    watch,
    setValue,
    reset,
  });

  if (revaluationOption === InvestmentRevaluationOption.DoNotRevalue) {
    return (
      <FullHeightView
        actionBar={
          <Row centered justify="space-between" spacing="sm">
            <Button variant="outlined" onClick={onPreviousStep} size="large">
              Previous step
            </Button>
            <Row centered spacing="sm">
              <Button variant="outlined" onClick={onSaveDraft} size="large">
                Save draft
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  onSaveAndApprove({
                    newRevaluations: [],
                    revaluations: [],
                    investmentHistory: [],
                  })
                }
                size="large"
              >
                Save and approve
              </Button>
            </Row>
          </Row>
        }
      >
        {children}
      </FullHeightView>
    );
  }

  return (
    <>
      {children}
      <RevaluationForm
        assetName={asset.name}
        assetId={asset.id}
        vehicleId={vehicleId}
        holdingClassInvestmentTotals={holdingClassInvestmentTotals}
        viewOnly={viewOnly}
        onSuccess={(data) => {
          onSaveAndApprove({
            newRevaluations: data.newRevaluations,
            revaluations: data.revaluations,
            investmentHistory: data.investmentHistory,
          });
        }}
        renderButtons={({ onSubmit, onError, handleSubmit }) => (
          <Row centered justify="space-between" spacing="sm">
            <Button variant="outlined" onClick={onPreviousStep} size="large">
              Previous step
            </Button>
            <Row centered spacing="sm">
              <Button variant="outlined" onClick={onSaveDraft} size="large">
                Save draft
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit, onError)}
                size="large"
              >
                Save and approve
              </Button>
            </Row>
          </Row>
        )}
      />
    </>
  );
};

export default RevaluationFormContent;
